import {
  createColumnHelper,
  type AccessorKeyColumnDef,
  type Column,
} from '@tanstack/react-table';

import type {
  FieldComponentProps,
  FilterProperty,
} from '@/types/advanceFilter';

const columnHelper = createColumnHelper<unknown>();

export const createColumns = <T>(properties: FilterProperty[]) => {
  return properties
    .filter((x) => x.column)
    .map(
      (x) =>
        columnHelper.accessor(x.name, {
          header: x.displayName,
          ...x.column,
          meta: {
            ...x.column?.meta,
            properties: [x, ...(x.linkedProperties || [])],
          },
        }) as AccessorKeyColumnDef<T>
    );
};

export interface PropertyField<T> {
  id: string;
  group: string;
  getTitle: (entity: T) => string;
  Component: (props: FieldComponentProps<T>) => React.ReactNode;
}

export const createFields = <T>(
  properties: FilterProperty<T>[]
): PropertyField<T>[] => {
  return properties
    .filter((x) => x.field)
    .map((property) => ({
      id: property.name,
      group: property.group,
      getTitle: (entity: T) =>
        property.field!.getTitle?.(entity) || property.displayName,
      Component: property.field!.Component,
    }));
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export const findPropertyInObject = <T>(
  obj: unknown,
  propertyName: string,
  nestedLevels = 1
): T | undefined => {
  if (!obj || typeof obj !== 'object' || obj === null) {
    return undefined;
  }

  const objRecord = obj as Record<string, unknown>;

  if (propertyName in objRecord && objRecord[propertyName] !== undefined) {
    return objRecord[propertyName] as T;
  }

  if (nestedLevels > 0) {
    for (const key in objRecord) {
      if (
        Object.prototype.hasOwnProperty.call(objRecord, key) &&
        typeof objRecord[key] === 'object'
      ) {
        const result = findPropertyInObject<T>(
          objRecord[key],
          propertyName,
          nestedLevels - 1
        );
        if (result !== undefined) {
          return result;
        }
      }
    }
  }

  return undefined;
};

export const getProperties = <T>(column: Column<T>): FilterProperty[] => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (column.columnDef.meta as any)?.properties || [];
};
